import { persistContinuityDefaults, type ContinuitySeed } from "./continuity.js";
import {
  DEFAULT_ORGX_SKILL_PACKS,
  installOrgxSkills,
  type SkillsInstallReport,
} from "./skills.js";
import { listOrgxPluginStatuses } from "./plugins.js";
import {
  runWorkspaceSetup,
  type WorkspaceSetupOptions,
  type WorkspaceSetupPrompts,
  type WorkspaceSetupResult,
} from "./setup-workspace.js";
import { listSurfaceStatuses, setupDetectedSurfaces, type SurfaceMutationResult } from "../surfaces/registry.js";
import {
  createWorkspace,
  getCurrentWorkspace,
  listWorkspaces,
  setDefaultWorkspace,
  type OrgxWorkspace,
} from "./workspaces.js";
import type { WizardStateRecord } from "./wizard-state.js";

export interface FounderPresetResult {
  continuity: WizardStateRecord;
  skillReport: SkillsInstallReport;
  surfaceResults: SurfaceMutationResult[];
  workspace: OrgxWorkspace | null;
  workspaceSetup?: WorkspaceSetupResult;
}

export async function runFounderPreset(
  prompts: WorkspaceSetupPrompts,
  options: WorkspaceSetupOptions & { pluginTargets?: readonly string[] },
): Promise<FounderPresetResult | WorkspaceSetupResult> {
  const surfaceResults = await setupDetectedSurfaces();
  const pluginTargets = options.pluginTargets ?? (
    await listOrgxPluginStatuses()
  )
    .filter((status) => status.installed)
    .map((status) => status.target);
  const skillReport = await installOrgxSkills({
    pluginTargets,
    skillNames: [...DEFAULT_ORGX_SKILL_PACKS],
  });

  let workspaceSetup: WorkspaceSetupResult | undefined;
  let workspace = await getCurrentWorkspace().catch(() => null);

  if (workspace || options.interactive) {
    workspaceSetup = await runWorkspaceSetup(
      {
        createWorkspace,
        getCurrentWorkspace,
        listWorkspaces,
        setDefaultWorkspace,
      },
      prompts,
      options,
    );

    if (workspaceSetup.status === "cancelled") {
      return workspaceSetup;
    }

    workspace = workspaceSetup.workspace ?? workspace ?? await getCurrentWorkspace().catch(() => null);
  }

  const continuity = persistContinuityDefaults({
    statuses: listSurfaceStatuses(),
    workspace,
  });

  return {
    continuity,
    skillReport,
    surfaceResults,
    workspace,
    ...(workspaceSetup ? { workspaceSetup } : {}),
  };
}
